<template>
  <p class="text-base font-light">{{ $t('settings.workOfflineDesc') }}</p>
  <section class="max-w-md">
    <p class="mb-3 text-lg text-gray-900">{{ $t('settings.apps') }}</p>

    <div v-for="app in mwApplications" :key="app.id" class="py-4 border-b first-of-type:border-t">
      <BaseToggle :name="app.id" :label="app.name" light v-model="app.worksOffline" />
    </div>

    <BaseButton outline class="w-48 mt-8 text-white border-0 bg-theme-600" @click="onSave">{{
      $t('common.actions.save')
    }}</BaseButton>
  </section>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';
import { UpdateMWApplicationCollection } from '@/services/api/mw';
import { deepCopy } from '@/utils/data';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import type { MWApplication } from '@/types/MW';

const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

const { mwApplications } = storeToRefs(useLookupsStore());
const originalApplications = ref<MWApplication[]>(deepCopy<MWApplication[]>(mwApplications.value));
const modifiedApplications = ref<MWApplication[]>([]);

watch(
  () => mwApplications.value,
  () => {
    if (!originalApplications.value.length) return;
    mwApplications.value.forEach((modifiedApp, index) => {
      const originalApp = originalApplications.value[index];
      if (originalApp.worksOffline !== modifiedApp.worksOffline) {
        const existingModuleIdx = modifiedApplications.value.findIndex(
          (app) => app.id == modifiedApp.id
        );

        if (existingModuleIdx === -1) modifiedApplications.value.push(modifiedApp);
        else modifiedApplications.value[existingModuleIdx] = modifiedApp;
      }
    });
  },
  { deep: true }
);

async function onSave() {
  if (modifiedApplications.value.length == 0) return;
  const res = await UpdateMWApplicationCollection(modifiedApplications.value);
  if (res) toastStore.success({ text: `${$t('common.toast.success')}` });
  else toastStore.error({ text: `${$t('common.toast.error')}` });
}
</script>
